<template>
  <div v-if="currentAgency && conversationStatus" class="conversation-info rounded px-3 py-3">
    <div class="mb-3 px-2">
      <div class="row pb-1">
        <div class="col-4 pt-1 text-nowrap">
          <h6>Initiated</h6>
        </div>
        <div class="col-8 ">
          <span class="text-nowrap">
            {{
              formatTimestamp(currentConversation.last_message_at || currentConversation.created_at, "MMM D, YYYY h:mm a")
            }}
          </span>
          <div
            v-if="currentConversation.status == 'active'"
            class="font-italic text-muted"
            style="font-size:70%;"
          >
            {{ formatTimestamp(currentConversation.created_at, 'fromnow') }}
          </div>
        </div>
      </div>
      <div class="row pb-1">
        <div class="col-4 pt-1 text-nowrap">
          <h6>Agent</h6>
        </div>
        <div class="col-8  text-nowrap">
          <span
            v-if="currentConversation.status == 'active' && currentConversation.operator_id === currentUser.user_id"
            title="Reassign Agent"
            style="cursor: pointer"
            @click="openChangeAgentModal()"
          >
            <u>{{currentConversation.operator_name }}</u>
          </span>
          <span v-else>{{ currentConversation.operator_name }}</span>
        </div>
      </div>
      <div class="row pb-1">
        <div class="col-4 pt-1 text-nowrap">
          <h6>Language</h6>
        </div>
        <div class="col-8 text-nowrap">
          <span
            v-if="currentConversation.channel != 'media' && currentConversation.status == 'active' && currentConversation.operator_id === currentUser.user_id"
            title="Update Recipient Language"
            style="cursor: pointer"
            @click="openChangeLangModal()"
          >
            <u>{{ recipientLanguage }}</u>
          </span>
          <span v-else>
            {{ recipientLanguage !== 'Set Language' ? recipientLanguage : '' }}
          </span>
        </div>
      </div>
      <div v-if="currentConversation.channel != 'media'" class="row pb-1">
        <div class="col-4 pt-1 text-nowrap">
          <h6>Direction</h6>
        </div>
        <div class="col-8 ">
          <span class="text-nowrap">
            {{ currentConversation.direction ? currentConversation.direction : 'outbound' }}
          </span>
        </div>
      </div>
      <div class="row pb-1">
        <div class="col-4 pt-1 text-nowrap">
          <h6>Channel</h6>
        </div>
        <div class="col-8 ">
          <span class="text-nowrap">
            {{ currentConversation.channel }}
          </span>
        </div>
      </div>
      <div v-if="currentConversation.channel != 'media'" class="row">
        <div class="col-4 pt-1 text-nowrap">
          <h6>Number</h6>
        </div>
        <div class="col-8 mt-1 text-truncate">
          <span class="text-nowrap">
            {{ prettyPhoneNumber(currentConversation.number) }}
          </span>
        </div>
      </div>
      <div class="row" v-if="currentConversation.channel == 'media'">
        <div class="col-4 pt-1 text-nowrap">
          <h6>Media File</h6>
        </div>
        <div class="col-8 text-truncate">
          <span :title="currentConversation.resource_file">
            <a :href="currentConversation.resource_file" target="_blank">
              {{ splitFilename(currentConversation.resource_file) }}
            </a>
          </span>
        </div>
      </div>
      <div class="row" v-if="currentConversation.channel == 'media'">
        <div class="col-4 pt-1 text-nowrap">
          <h6>Participants</h6>
        </div>
        <div
          v-if="currentConversation.recipients && currentConversation.recipients.length > 0"
          class="col-8"
        >
          {{ currentConversation.recipients.length }}
        </div>
      </div>
    </div>

    <AssignConversationButton
      v-if="
        currentConversation.status == 'active' &&
        agencySettings.enableConversationAssignment === true &&
        currentConversation.operator_id === null &&
        $auth.can('assign:conversation')
      "
      :conversationId="currentConversation.conversation_id"
      class="mb-2"
    />
    <UnassignConversationButton
      v-if="
        currentConversation.status == 'active' &&
        agencySettings.enableConversationAssignment === true &&
        (currentConversation.operator_id === currentUser.user_id || (currentConversation.operator_id !== null && $auth.can('admin:agency')))
      "
      :conversationId="currentConversation.conversation_id"
      class="mb-2"
    />
    <CloseConversationButton
      v-if="generalConditionForCTA"
      :conversationId="currentConversation.conversation_id"
      class="mb-2"
    />
    <TransferConversationButton
      v-if="currentConversation.tcc_session && generalConditionForCTA"
      :conversationId="currentConversation.conversation_id"
      :sessionId="currentConversation.tcc_session.session_id"
      class="mb-2"
    />
  </div>
</template>

<script>
/* eslint-disable */
import { EventBus } from "../../events/eventBus";
import { mapGetters } from "vuex";
import CloseConversationButton from "../action_buttons/CloseConversationButton";
import AssignConversationButton from "../action_buttons/AssignConversationButton";
import UnassignConversationButton from "../action_buttons/UnassignConversationButton";
import TransferConversationButton from "../action_buttons/TransferConversationButton";
import RecipientLanguageForm from "./RecipientLanguageForm";
import ConversationAgentForm from "./ConversationAgentForm";
import formatter from "../../mixins/formatter";

export default {
  name: "ConversationInfo",
  mixins: [formatter],
  props: {
    currentRecipientLang: String,
    conversationId: String,
  },
  components: {
    CloseConversationButton,
    AssignConversationButton,
    UnassignConversationButton,
    TransferConversationButton
  },
  computed: {
    ...mapGetters("user", ["currentToken"]),
    ...mapGetters("agency", ["currentAgency", "textDialects", "agencySettings"]),
    ...mapGetters("conversations", ["currentConversation", "conversationStatus"]),
    ...mapGetters("user", ["currentUser"]),
    internationalDialCodeEnabled() {
      return this.agencySettings.enableInternational == "true";
    },
    recipientLanguage() {
      if (this.currentConversation.channel === "media") {
        let langLabels = new Set();
        this.currentConversation.recipients.forEach((r) => {
          if (r.language && r.language !== "")
            langLabels.add(this.dialectLabel(r.language));
        })
        return Array.from(langLabels).join(", ");
      }
      else
        return this.dialectLabel(this.currentRecipientLang)
    },
    generalConditionForCTA() {
      const { status, operator_id } = this.currentConversation;
      const { enableConversationAssignment } = this.agencySettings;

      if (status === 'active') {
        if (enableConversationAssignment === false) {
          return true;
        }
        if (enableConversationAssignment === true && (operator_id === this.currentUser.user_id || (operator_id !== null && this.$auth.can('admin:agency')))) {
          return true;
        }
      }
      return false;
    }
  },
  created() { },
  methods: {
    dialectLabel(code) {
      if (!!code) {
        const langCode = code.toLowerCase();

        let languageLabel = langCode;
        try {
          languageLabel = this.textDialects.find(o => o.code === langCode).label;
        } catch (error) {
          return langCode;
        }

        return languageLabel;
      } else {
        return "Set Language";
      }
    },
    openChangeLangModal() {
      const $ = window.$;
      $("#id-modal-container").modal({
        backdrop: "static"
      });
      EventBus.$emit("open", {
        component: RecipientLanguageForm,
        title: "Change Recipient Language",
        closeOnClick: true,
        props: {
          language: this.currentRecipientLang
        }
      });
    },
    openChangeAgentModal() {
      const $ = window.$;
      $("#id-modal-container").modal({
        backdrop: "static"
      });
      EventBus.$emit("open", {
        component: ConversationAgentForm,
        title: "Reassign Agent",
        closeOnClick: true,
        props: {
          conversationId: this.currentConversation.conversation_id,
          operatorId: this.currentConversation.operator_id
        }
      });
    }
  },
};
</script>

<style lang="scss" scoped>
.conversation-info {
  width: 100%;
  max-width: 1024px;

  .card {
    .card-body {
      padding: 1rem;
    }
  }
}

div .fa-3x {
  text-align: center;
  margin-top: 50px;
}

div .chat-message-translated {
  color: #8f8f8f;
  margin-top: 10px;
}

.chat-message-translated-lang {
  font-size: small;
  font-style: italic;
  color: #8f8f8f;
}
</style>
